import { isAboutPage } from './about_pages'

const ROBOTS_INDEX = 'index, follow'
const ROBOTS_NOINDEX_FOLLOW = 'noindex, follow'
const ROBOTS_NOINDEX_NOFOLLOW = 'noindex, nofollow'

type EntityState = 'loading' | 'present' | 'missing'

type IndexabilityReport = Readonly<{
  entity?: EntityState
  browseSettled?: boolean
  browseResultCount?: number
}>

type IndexabilityReason =
  | 'public'
  | 'auth'
  | 'account'
  | 'checkout'
  | 'private'
  | 'unknown_route'
  | 'missing_entity'
  | 'empty_browse'
  | 'low_quality_browse'

type IndexabilityDecision = Readonly<{
  robots: string
  reason: IndexabilityReason
  indexable: boolean
}>

type CategoryRouteTagId = number | 'invalid' | undefined

type PrivateRoute = Readonly<{
  pattern: RegExp
  reason: IndexabilityReason
}>

// Logged-in and transactional surfaces. nofollow so they do not pass equity.
const privateRoutes: readonly PrivateRoute[] = [
  { pattern: /^\/login$/, reason: 'auth' },
  { pattern: /^\/register(?:\/[^/]+)?$/, reason: 'auth' },
  { pattern: /^\/forgot-password$/, reason: 'auth' },
  { pattern: /^\/reset-password$/, reason: 'auth' },
  { pattern: /^\/account(?:\/.*)?$/, reason: 'account' },
  { pattern: /^\/admin(?:\/.*)?$/, reason: 'account' },
  { pattern: /^\/(?:u|collections)\/me(?:\/.*)?$/, reason: 'account' },
  { pattern: /^\/orders(?:\/.*)?$/, reason: 'checkout' },
  { pattern: /^\/subscriptions(?:\/.*)?$/, reason: 'checkout' },
  { pattern: /^\/sell(?:\/.*)?$/, reason: 'private' },
  { pattern: /^\/photos(?:\/.*)?$/, reason: 'private' },
  { pattern: /^\/items\/[^/]+\/listing$/, reason: 'private' },
  { pattern: /^\/items\/[^/]+\/unit\/[^/]+\/listing$/, reason: 'private' },
  { pattern: /^\/item\/[^/]+\/condition$/, reason: 'private' },
  { pattern: /^\/categories\/[^/]+\/items\/[^/]+\/ob$/, reason: 'private' },
  { pattern: /^\/(?:u|collections)\/[^/]+\/blog\/new$/, reason: 'private' },
  { pattern: /^\/(?:u|collections)\/[^/]+\/blog\/[^/]+\/edit$/, reason: 'private' },
]

const idPattern = '[1-9]\\d*'

const publicPatterns: readonly RegExp[] = [
  /^\/$/,
  /^\/categories$/,
  new RegExp(`^/categories/${idPattern}$`),
  new RegExp(`^/categories/${idPattern}/items/${idPattern}$`),
  new RegExp(`^/(?:u|collections)/${idPattern}$`),
  new RegExp(`^/(?:u|collections)/${idPattern}/about$`),
  new RegExp(`^/(?:u|collections)/${idPattern}/reviews$`),
  new RegExp(`^/(?:u|collections)/${idPattern}/blog$`),
  new RegExp(`^/(?:u|collections)/${idPattern}/blog/${idPattern}$`),
  new RegExp(`^/(?:u|collections)/${idPattern}/units/${idPattern}$`),
]

const browsePatterns: readonly RegExp[] = [
  /^\/$/,
  /^\/categories$/,
  new RegExp(`^/categories/${idPattern}$`),
  new RegExp(`^/(?:u|collections)/${idPattern}$`),
]

const normalizePath = (pathname: string): string => {
  if (pathname.length > 1 && pathname.endsWith('/')) {
    return pathname.replace(/\/+$/, '')
  }
  return pathname || '/'
}

const searchParamsFrom = (search: string): URLSearchParams => {
  const raw = search.startsWith('?') ? search.slice(1) : search
  return new URLSearchParams(raw)
}

const hasMeaningfulSearch = (search: string): boolean => {
  let meaningful = false
  searchParamsFrom(search).forEach((value, key) => {
    if (key !== '' && value.trim() !== '') meaningful = true
  })
  return meaningful
}

const searchQuery = (search: string): string => {
  return (searchParamsFrom(search).get('query') ?? '').trim()
}

const isPublicPath = (pathname: string): boolean => {
  if (publicPatterns.some(pattern => pattern.test(pathname))) return true
  const about = pathname.match(/^\/about\/([^/]+)$/)
  return about ? isAboutPage(about[1]) : false
}

const isBrowseSurface = (pathname: string): boolean => {
  return browsePatterns.some(pattern => pattern.test(pathname))
}

const decision = (robots: string, reason: IndexabilityReason): IndexabilityDecision => {
  return {
    robots,
    reason,
    indexable: reason === 'public',
  }
}

const resolveIndexability = (input: Readonly<{
  pathname: string
  search?: string
  report?: IndexabilityReport | null
}>): IndexabilityDecision => {
  const pathname = normalizePath(input.pathname)
  const search = input.search ?? ''
  const report = input.report ?? null

  const privateRoute = privateRoutes.find(route => route.pattern.test(pathname))
  if (privateRoute) {
    return decision(ROBOTS_NOINDEX_NOFOLLOW, privateRoute.reason)
  }

  if (!isPublicPath(pathname)) {
    return decision(ROBOTS_NOINDEX_FOLLOW, 'unknown_route')
  }

  if (report?.entity === 'missing') {
    return decision(ROBOTS_NOINDEX_FOLLOW, 'missing_entity')
  }

  if (hasMeaningfulSearch(search)) {
    const query = searchQuery(search)
    if (
      isBrowseSurface(pathname) &&
      query.length > 0 &&
      report?.browseSettled === true &&
      (report.browseResultCount ?? 0) === 0
    ) {
      return decision(ROBOTS_NOINDEX_FOLLOW, 'empty_browse')
    }
    return decision(ROBOTS_NOINDEX_FOLLOW, 'low_quality_browse')
  }

  return decision(ROBOTS_INDEX, 'public')
}

const sameIndexabilityReport = (left: IndexabilityReport | null, right: IndexabilityReport | null): boolean => {
  if (left === right) return true
  if (!left || !right) return false
  return left.entity === right.entity &&
    left.browseSettled === right.browseSettled &&
    left.browseResultCount === right.browseResultCount
}

const mergeIndexabilityReports = (reports: ReadonlyArray<IndexabilityReport | null>): IndexabilityReport | null => {
  let missing = false
  let entity: EntityState | undefined
  let browseSettled: boolean | undefined
  let browseResultCount: number | undefined
  let sawBrowse = false

  reports.forEach(entry => {
    if (!entry) return
    if (entry.entity === 'missing') missing = true
    else if (entry.entity) entity = entry.entity
    if (entry.browseSettled !== undefined || entry.browseResultCount !== undefined) {
      sawBrowse = true
      browseSettled = entry.browseSettled
      browseResultCount = entry.browseResultCount
    }
  })

  if (missing) return { entity: 'missing' }
  if (sawBrowse) return { entity, browseSettled, browseResultCount }
  if (entity) return { entity }
  return null
}

const categoryRouteTagId = (raw: string | undefined): CategoryRouteTagId => {
  if (raw === undefined || raw === '') return undefined
  if (!/^[1-9]\d*$/.test(raw)) return 'invalid'
  const id = Number(raw)
  if (!Number.isSafeInteger(id)) return 'invalid'
  return id
}

const isMissingCategoryRoute = (routeTagId: CategoryRouteTagId, missingTagId: number | null): boolean => {
  if (routeTagId === 'invalid') return true
  return typeof routeTagId === 'number' && missingTagId === routeTagId
}

const applyRobotsMeta = (resolved: IndexabilityDecision, doc: Document = document): void => {
  const head = doc.head
  if (!head) return

  let meta = head.querySelector('meta[name="robots"]')
  if (!meta) {
    meta = doc.createElement('meta')
    meta.setAttribute('name', 'robots')
    head.appendChild(meta)
  }
  meta.setAttribute('content', resolved.robots)
  meta.setAttribute('data-reason', resolved.reason)
}

export {
  ROBOTS_INDEX,
  ROBOTS_NOINDEX_FOLLOW,
  ROBOTS_NOINDEX_NOFOLLOW,
  applyRobotsMeta,
  categoryRouteTagId,
  isMissingCategoryRoute,
  mergeIndexabilityReports,
  resolveIndexability,
  sameIndexabilityReport,
}
export type { CategoryRouteTagId, IndexabilityDecision, IndexabilityReason, IndexabilityReport }
